import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import Typography from 'kingpin/atoms/Typography';

import {
  SubtextPlaceholder,
  getPlaceholderQuery,
  matchPlaceholders,
} from './subtextPlaceholders';

// Suggestions for a {placeholder} as it's typed, shared by the subtext input
// and the rich text editor. Each editor tells the hook where its caret is and
// inserts the chosen placeholder its own way.
export const usePlaceholderSuggestions = ({
  placeholders,
  onChoose,
}: {
  placeholders: SubtextPlaceholder[];
  // Replace the partly typed placeholder, from start to the caret
  onChoose: (args: { placeholder: SubtextPlaceholder; start: number }) => void;
}) => {
  const [typing, setTyping] = useState<
    { start: number; query: string } | undefined
  >(undefined);
  const [activeIndex, setActiveIndex] = useState<number>(0);

  const matches = useMemo(
    () =>
      typing ? matchPlaceholders({ placeholders, query: typing.query }) : [],
    [placeholders, typing],
  );
  const isOpen = matches.length > 0;

  // A new query starts from the best match
  useEffect(() => {
    setActiveIndex(0);
  }, [typing]);

  const update = useCallback(
    ({ text, caret }: { text: string; caret: number }) => {
      const next = getPlaceholderQuery({ text, caret });
      setTyping((current) => (_.isEqual(current, next) ? current : next));
    },
    [],
  );
  const close = useCallback(() => setTyping(undefined), []);
  const choose = useCallback(
    (placeholder: SubtextPlaceholder) => {
      if (typing) {
        onChoose({ placeholder, start: typing.start });
      }
      setTyping(undefined);
    },
    [onChoose, typing],
  );

  // Arrows move through the suggestions, Enter or Tab chooses, Escape
  // closes. Other keys are left to the editor. True when it took the key.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!isOpen) {
        return false;
      }
      const handled = (() => {
        switch (e.key) {
          case 'ArrowDown':
            setActiveIndex((i) => (i + 1) % matches.length);
            return true;
          case 'ArrowUp':
            setActiveIndex((i) => (i - 1 + matches.length) % matches.length);
            return true;
          case 'Enter':
          case 'Tab':
            choose(matches[Math.min(activeIndex, matches.length - 1)]);
            return true;
          case 'Escape':
            close();
            return true;
          default:
            return false;
        }
      })();
      if (handled) {
        e.preventDefault();
        e.stopPropagation();
      }
      return handled;
    },
    [activeIndex, choose, close, isOpen, matches],
  );

  return {
    isOpen,
    matches,
    activeIndex,
    setActiveIndex,
    update,
    close,
    choose,
    onKeyDown,
  };
};

const Menu = styled.div`
  position: absolute;
  z-index: 20;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 280px;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid var(--color-border-primary);
  background-color: var(--color-bg-primary);
  box-shadow: var(--shadow-dropdown);
`;

const Option = styled.div<{ $isActive: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  background-color: ${(props) =>
    props.$isActive ? 'var(--color-bg-hover)' : 'transparent'};
`;

export const PlaceholderSuggestionList = ({
  matches,
  activeIndex,
  position,
  onHover,
  onChoose,
}: {
  matches: SubtextPlaceholder[];
  activeIndex: number;
  // Within the nearest positioned ancestor
  position: { top: number | string; left: number | string };
  onHover: (index: number) => void;
  onChoose: (placeholder: SubtextPlaceholder) => void;
}) => (
  <Menu
    style={position}
    role="listbox"
    data-testid="studio-placeholder-suggestions"
    // Keeps focus (and the caret) in the editor
    onMouseDown={(e) => e.preventDefault()}
  >
    {matches.map((p, index) => (
      <Option
        key={p.key}
        role="option"
        aria-selected={index === activeIndex}
        $isActive={index === activeIndex}
        onMouseEnter={() => onHover(index)}
        onClick={() => onChoose(p)}
        data-testid={`studio-placeholder-suggestion-${p.key}`}
      >
        <Typography.Body type="Body 13" isEllipsis>
          {`{${p.key}}`}
        </Typography.Body>
        <Typography.Body
          type="Body 12"
          color="var(--color-text-tertiary)"
          isEllipsis
        >
          {p.name}
        </Typography.Body>
      </Option>
    ))}
  </Menu>
);
